import { useEffect, useMemo, useState, useCallback } from "react";
import Swal from "sweetalert2";
import "../css/Dashboard.css";
import "../css/Notifications.css";
import useIdleLogout from "../hooks/useIdleLogout";

/* ---------------- CSV Export helpers ---------------- */
function csvEscape(cell) {
  const s = String(cell ?? "");
  const needsQuote = /[",\n]/.test(s);
  const esc = s.replace(/"/g, '""');
  return needsQuote ? `"${esc}"` : esc;
}
function downloadCSV(filename, rows) {
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + rows.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ---------------- ENV ---------------- */
const TAX_BASE = (process.env.REACT_APP_RULES_API_BASE || "").replace(/\/+$/, "");
const TAX_RULES_API = TAX_BASE ? `${TAX_BASE}/tax-rules` : "";
const RIDE_TYPES_API = (process.env.REACT_APP_RIDETYPES_API_GET_URL || "").replace(
  /\/+$/,
  ""
);

/* ---------------- helpers ---------------- */
const toLocal = (s) => {
  if (!s) return "-";
  return String(s).replace("T", " ").replace(".000Z", "");
};

async function fetchJson(url, opts = {}, timeoutMs = 15000) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { ...opts, signal: controller.signal });
    const text = await res.text();
    let json = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }

    if (!res.ok) {
      const msg =
        json?.message ||
        json?.error ||
        json?.errors?.[0] ||
        `Request failed (HTTP ${res.status})`;
      throw new Error(msg);
    }

    return json ?? {};
  } finally {
    clearTimeout(t);
  }
}

function getAuthHeaders(withJson = false) {
  const t = (localStorage.getItem("access_token") || "").trim();
  const h = { Accept: "application/json" };
  if (withJson) h["Content-Type"] = "application/json";
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

const TAX_TYPES = ["VAT", "GST", "TDS", "DST", "LOCAL_SURCHARGE"];
const TAXABLE_BASES = [
  "platform_fee",
  "fare_subtotal",
  "fare_after_discounts",
  "driver_earnings",
];

const clampNum = (v, min, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.max(min, Math.min(max, n));
};
const clampInt = (v, min, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const x = Math.trunc(n);
  return Math.max(min, Math.min(max, x));
};

function isFinanceUser() {
  try {
    if (localStorage.getItem("is_finance") === "true") return true;
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const raw = user?.role ?? user?.user_role ?? user?.role_name ?? user?.type ?? "";
    return String(raw).toLowerCase().replace(/\s+/g, " ").trim() === "finance";
  } catch { return false; }
}

export default function TaxManagement() {
  useIdleLogout();
  const readOnly = isFinanceUser();

  /* ---------------- SERVICE TYPES (from ride types API) ---------------- */
  const [serviceOptions, setServiceOptions] = useState([]); // [{value,label}]
  const [serviceLoading, setServiceLoading] = useState(false);
  const [serviceError, setServiceError] = useState("");

  const normalizeRideTypes = (payload) => {
    const arr = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.ride_types)
      ? payload.ride_types
      : [];

    const pick = (o, keys) => {
      for (const k of keys) {
        const v = o?.[k];
        if (v != null && String(v).trim() !== "") return String(v).trim();
      }
      return "";
    };

    const items = arr
      .map((x) => {
        if (x == null) return null;
        if (typeof x === "string" || typeof x === "number") {
          const s = String(x).trim();
          return s ? { value: s, label: s } : null;
        }

        const value = pick(x, [
          "service_type",
          "ride_type",
          "ride_type_name",
          "name",
          "title",
          "type_name",
          "rideType",
          "rideTypeName",
        ]);

        const label = pick(x, [
          "display_name",
          "ride_type_name",
          "service_type",
          "ride_type",
          "name",
          "title",
        ]);

        const v = value || label;
        if (!v) return null;
        return { value: v, label: label || v };
      })
      .filter(Boolean);

    const seen = new Set();
    const dedup = [];
    for (const it of items) {
      const k = it.value.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      dedup.push(it);
    }

    dedup.sort((a, b) => a.label.localeCompare(b.label));
    return dedup;
  };

  const loadServiceTypes = useCallback(async () => {
    setServiceError("");
    if (!RIDE_TYPES_API) {
      setServiceOptions([]);
      setServiceError(
        "Ride types API is not configured. Set REACT_APP_RIDETYPES_API_GET_URL."
      );
      return;
    }

    try {
      setServiceLoading(true);
      const json = await fetchJson(RIDE_TYPES_API, {
        headers: getAuthHeaders(false),
      });

      const opts = normalizeRideTypes(json);
      setServiceOptions(opts);
    } catch (e) {
      setServiceOptions([]);
      setServiceError(e.message || "Failed to load service types.");
    } finally {
      setServiceLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServiceTypes(); // auto fetch once on load
  }, [loadServiceTypes]);

  /* ---------------- FORM (CREATE) STATE ---------------- */
  const [serviceType, setServiceType] = useState(""); // "" => any
  const [countryCode, setCountryCode] = useState("BT");
  const [cityId, setCityId] = useState("THIMPHU");
  const [taxType, setTaxType] = useState("GST");
  const [ratePercent, setRatePercent] = useState("5"); // 0..100
  const [taxableBase, setTaxableBase] = useState("platform_fee");
  const [inclusiveMode, setInclusiveMode] = useState("exclusive"); // exclusive | inclusive
  const [priority, setPriority] = useState("10"); // 1..10
  const [isActive, setIsActive] = useState(true);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");

  /* ---------------- LIST STATE ---------------- */
  const [taxRules, setTaxRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingNew, setSavingNew] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  /* ---------------- INLINE EDIT STATE ---------------- */
  const [rowEditId, setRowEditId] = useState(null);
  const [rowDraft, setRowDraft] = useState(null);
  const [rowSaving, setRowSaving] = useState(false);

  const wideEdge = { marginInline: "clamp(-16px, -2.5vw, -32px)" };
  const TOP_PAD = { paddingInline: "clamp(16px, 2.5vw, 32px)" };

  const rateBp = useMemo(() => {
    const n = Number(ratePercent);
    if (!Number.isFinite(n)) return 0;
    const clamped = Math.max(0, Math.min(100, n));
    return Math.round(clamped * 100);
  }, [ratePercent]);

  const mapRule = (r) => ({
    id: r?.id ?? r?.tax_rule_id ?? null,
    country_code: r?.country_code ?? null,
    city_id: r?.city_id ?? null,
    service_type: r?.service_type ?? null,
    tax_type: r?.tax_type ?? null,
    rate_percent_bp: Number(r?.rate_percent_bp ?? 0),
    tax_inclusive: Number(r?.tax_inclusive ?? 0),
    taxable_base: r?.taxable_base ?? null,
    priority: Number(r?.priority ?? 0),
    is_active: Number(r?.is_active ?? 0),
    starts_at: r?.starts_at ?? null,
    ends_at: r?.ends_at ?? null,
    created_at: r?.created_at ?? null,
  });

  /* ---------------- LOAD LIST ---------------- */
  const loadRules = useCallback(async () => {
    if (!TAX_RULES_API) {
      setError(
        "Tax rules API is not configured. Please set REACT_APP_RULES_API_BASE."
      );
      setTaxRules([]);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const url = new URL(TAX_RULES_API, window.location.origin);
      url.searchParams.set("limit", "50");
      url.searchParams.set("offset", "0");

      const json = await fetchJson(url.toString(), {
        headers: getAuthHeaders(false),
      });

      const arr = Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json)
        ? json
        : [];
      setTaxRules(arr.map(mapRule));

      if (rowEditId) {
        const exists = arr.some((x) => (x?.id ?? x?.tax_rule_id) === rowEditId);
        if (!exists) {
          setRowEditId(null);
          setRowDraft(null);
        }
      }
    } catch (e) {
      setError(e.message || "Failed to load tax rules.");
      setTaxRules([]);
    } finally {
      setLoading(false);
    }
  }, [rowEditId]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  /* ---------------- DATETIME CONVERT ---------------- */
  const toMysqlDatetime = (dtLocal) => {
    if (!dtLocal) return null;
    const s = String(dtLocal);
    if (!s.includes("T")) return s;
    const [d, t] = s.split("T");
    const hhmm = (t || "").slice(0, 5);
    return `${d} ${hhmm}:00`;
  };

  const mysqlToDtLocal = (v) => {
    if (!v) return "";
    const s = String(v).replace("T", " ");
    const [d, t] = s.split(" ");
    const hhmm = (t || "").slice(0, 5);
    return d && hhmm ? `${d}T${hhmm}` : "";
  };

  /* ---------------- INPUT HANDLERS (CLAMP) ---------------- */
  const handleRateChange = (val) => {
    if (val === "") return setRatePercent("");
    const n = clampNum(val, 0, 100);
    if (n === null) return;
    setRatePercent(String(n));
  };
  const handleRateBlur = () => {
    if (ratePercent === "") return;
    const n = clampNum(ratePercent, 0, 100);
    setRatePercent(String(n ?? 0));
  };

  const handlePriorityChange = (val) => {
    if (val === "") return setPriority("");
    const n = clampInt(val, 1, 10);
    if (n === null) return;
    setPriority(String(n));
  };
  const handlePriorityBlur = () => {
    if (priority === "") return;
    const n = clampInt(priority, 1, 10);
    setPriority(String(n ?? 10));
  };

  /* ---------------- CREATE NEW ---------------- */
  const handleSubmitNew = async (e) => {
    e.preventDefault();

    if (!TAX_RULES_API) {
      Swal.fire({
        icon: "error",
        title: "Missing API",
        text: "Set REACT_APP_RULES_API_BASE",
      });
      return;
    }

    const cc = countryCode.trim();
    const cid = cityId.trim();
    const pr = clampInt(priority, 1, 10);

    const rateN = clampNum(ratePercent, 0, 100);
    const rateBpSafe =
      rateN == null ? null : Math.round(Math.max(0, Math.min(100, rateN)) * 100);

    if (!cc) return Swal.fire({ icon: "error", title: "Country Code is required" });
    if (!startsAt) return Swal.fire({ icon: "error", title: "Starts At is required" });
    if (!taxType) return Swal.fire({ icon: "error", title: "Tax Type is required" });
    if (!taxableBase) return Swal.fire({ icon: "error", title: "Taxable Base is required" });
    if (rateBpSafe == null)
      return Swal.fire({ icon: "error", title: "Enter valid Tax Rate (%)" });
    if (rateN < 0 || rateN > 100)
      return Swal.fire({ icon: "error", title: "Tax Rate must be 0 to 100%" });
    if (pr == null)
      return Swal.fire({ icon: "error", title: "Priority must be a number" });
    if (pr < 1 || pr > 10)
      return Swal.fire({ icon: "error", title: "Priority must be between 1 and 10" });

    const payload = {
      country_code: cc,
      city_id: cid ? cid : null,
      service_type: serviceType ? serviceType : null,
      tax_type: taxType,
      rate_percent_bp: rateBpSafe,
      tax_inclusive: inclusiveMode === "inclusive" ? 1 : 0,
      taxable_base: taxableBase,
      priority: pr,
      is_active: isActive ? 1 : 0,
      starts_at: toMysqlDatetime(startsAt),
      ends_at: endsAt ? toMysqlDatetime(endsAt) : null,
    };

    try {
      setSavingNew(true);

      await fetchJson(TAX_RULES_API, {
        method: "POST",
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });

      Swal.fire({
        icon: "success",
        title: "Tax rule created",
        timer: 1200,
        showConfirmButton: false,
      });

      setStartsAt("");
      setEndsAt("");
      await loadRules();
    } catch (e2) {
      Swal.fire({
        icon: "error",
        title: "Save failed",
        text: e2.message || "Failed to save tax rule.",
      });
    } finally {
      setSavingNew(false);
    }
  };

  /* ---------------- INLINE EDIT ---------------- */
  const beginRowEdit = (r) => {
    setRowEditId(r.id);
    setRowDraft({
      country_code: r.country_code ?? "",
      city_id: r.city_id ?? "",
      service_type: r.service_type ?? "",
      tax_type: r.tax_type ?? "GST",
      rate_percent: ((Number(r.rate_percent_bp) || 0) / 100).toString(),
      tax_inclusive: Number(r.tax_inclusive) === 1 ? 1 : 0,
      taxable_base: r.taxable_base ?? "platform_fee",
      priority: String(r.priority ?? 10),
      is_active: Number(r.is_active) === 1 ? 1 : 0,
      starts_at: mysqlToDtLocal(r.starts_at),
      ends_at: mysqlToDtLocal(r.ends_at),
    });
  };

  const cancelRowEdit = () => {
    setRowEditId(null);
    setRowDraft(null);
  };

  const updateDraft = (key, val) => {
    setRowDraft((prev) => ({ ...(prev || {}), [key]: val }));
  };

  const updateDraftRate = (val) => {
    if (val === "") return updateDraft("rate_percent", "");
    const n = clampNum(val, 0, 100);
    if (n === null) return;
    updateDraft("rate_percent", String(n));
  };
  const blurDraftRate = () => {
    const cur = rowDraft?.rate_percent ?? "";
    if (cur === "") return;
    const n = clampNum(cur, 0, 100);
    updateDraft("rate_percent", String(n ?? 0));
  };

  const updateDraftPriority = (val) => {
    if (val === "") return updateDraft("priority", "");
    const n = clampInt(val, 1, 10);
    if (n === null) return;
    updateDraft("priority", String(n));
  };
  const blurDraftPriority = () => {
    const cur = rowDraft?.priority ?? "";
    if (cur === "") return;
    const n = clampInt(cur, 1, 10);
    updateDraft("priority", String(n ?? 10));
  };

  const saveRowEdit = async () => {
    if (!rowEditId || !rowDraft) return;
    if (!TAX_RULES_API) return;

    const cc = String(rowDraft.country_code || "").trim();
    const pr = clampInt(rowDraft.priority, 1, 10);

    const rateN = clampNum(rowDraft.rate_percent, 0, 100);
    const rateBpRow =
      rateN == null ? null : Math.round(Math.max(0, Math.min(100, rateN)) * 100);

    if (!cc) return Swal.fire({ icon: "error", title: "Country Code is required" });
    if (!rowDraft.starts_at) return Swal.fire({ icon: "error", title: "Starts At is required" });
    if (!rowDraft.tax_type) return Swal.fire({ icon: "error", title: "Tax Type is required" });
    if (!rowDraft.taxable_base) return Swal.fire({ icon: "error", title: "Taxable Base is required" });
    if (rateBpRow == null)
      return Swal.fire({ icon: "error", title: "Enter valid Tax Rate (%)" });
    if (rateN < 0 || rateN > 100)
      return Swal.fire({ icon: "error", title: "Tax Rate must be 0 to 100%" });
    if (pr == null)
      return Swal.fire({ icon: "error", title: "Priority must be a number" });
    if (pr < 1 || pr > 10)
      return Swal.fire({ icon: "error", title: "Priority must be between 1 and 10" });

    const payload = {
      country_code: cc,
      city_id: String(rowDraft.city_id || "").trim() || null,
      service_type: String(rowDraft.service_type || "").trim() || null,
      tax_type: rowDraft.tax_type,
      rate_percent_bp: rateBpRow,
      tax_inclusive: Number(rowDraft.tax_inclusive) === 1 ? 1 : 0,
      taxable_base: rowDraft.taxable_base,
      priority: pr,
      is_active: Number(rowDraft.is_active) === 1 ? 1 : 0,
      starts_at: toMysqlDatetime(rowDraft.starts_at),
      ends_at: rowDraft.ends_at ? toMysqlDatetime(rowDraft.ends_at) : null,
    };

    try {
      setRowSaving(true);

      await fetchJson(`${TAX_RULES_API}/${encodeURIComponent(rowEditId)}`, {
        method: "PUT",
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });

      Swal.fire({
        icon: "success",
        title: "Changes saved",
        timer: 1100,
        showConfirmButton: false,
      });

      cancelRowEdit();
      await loadRules();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Update failed",
        text: e.message || "Failed to update.",
      });
    } finally {
      setRowSaving(false);
    }
  };

  /* ---------------- DELETE ---------------- */
  const handleDelete = async (id) => {
    if (!TAX_RULES_API) return;

    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete this tax rule?",
      text: `Tax Rule ID: ${id}`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!ok.isConfirmed) return;

    try {
      setDeletingId(id);

      await fetchJson(`${TAX_RULES_API}/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: getAuthHeaders(false),
      });

      Swal.fire({
        icon: "success",
        title: "Deleted",
        timer: 1000,
        showConfirmButton: false,
      });

      if (rowEditId === id) cancelRowEdit();
      await loadRules();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text: e.message || "Failed to delete.",
      });
    } finally {
      setDeletingId(null);
    }
  };

  /* ---------------- EXPORT ---------------- */
  const exportAllToExcel = () => {
    const now = new Date();
    const ts = now.toISOString().replace(/[:.-]/g, "").slice(0, 15);
    const fname = `tax_rules_${ts}.csv`;

    const headerLines = [
      ["Report Title", "Tax Management Rules"],
      ["Exported At", now.toLocaleString()],
      [],
    ].map((row) => row.map(csvEscape).join(","));

    const tableHeader = [
      "SI.No",
      "ID",
      "Country",
      "City",
      "Service Type",
      "Tax Type",
      "Rate (%)",
      "Inclusive",
      "Taxable Base",
      "Priority",
      "Status",
      "Starts At",
      "Ends At",
    ]
      .map(csvEscape)
      .join(",");

    const rows = taxRules.map((r, idx) => {
      const ratePct = ((Number(r.rate_percent_bp) || 0) / 100).toFixed(2);
      return [
        String(idx + 1),
        r.id,
        r.country_code ?? "",
        r.city_id ?? "",
        r.service_type ?? "",
        r.tax_type ?? "",
        ratePct,
        Number(r.tax_inclusive) === 1 ? "Yes" : "No",
        r.taxable_base ?? "",
        r.priority ?? "",
        Number(r.is_active) === 1 ? "Active" : "Inactive",
        r.starts_at ?? "",
        r.ends_at ?? "",
      ]
        .map(csvEscape)
        .join(",");
    });

    downloadCSV(fname, [...headerLines, tableHeader, ...rows]);
  };

  /* ---------------- EXPORT BUTTON STYLE ---------------- */
  const EXPORT_GREEN = "#1e9e56";
  const exportBtnBase = {
    border: `2px solid ${EXPORT_GREEN}`,
    color: EXPORT_GREEN,
    background: "transparent",
    borderRadius: 999,
    fontWeight: 600,
    transition: "all 0.25s ease",
    padding: "6px 18px",
    letterSpacing: "0.3px",
    boxShadow: "none",
  };
  const exportBtnHover = {
    background: EXPORT_GREEN,
    color: "#ffffff",
    boxShadow: "none",
    transform: "translateY(-1px)",
  };
  const exportBtnDisabled = {
    opacity: 0.5,
    cursor: "not-allowed",
    boxShadow: "none",
    transform: "none",
  };
  const [isExportHover, setIsExportHover] = useState(false);
  const exportBtnStyle = {
    ...exportBtnBase,
    ...(isExportHover ? exportBtnHover : null),
    ...(!taxRules.length ? exportBtnDisabled : null),
  };

  const serviceSelectDisabled = serviceLoading || !serviceOptions.length;

  return (
    <div className="p-4 flex-grow-1">
      <style>{`
        .tax-table th { white-space: nowrap; }
        .tax-table { table-layout: fixed; }
        .tax-table td, .tax-table th { vertical-align: middle; }

        .tax-inline-input,
        .tax-inline-select {
          width: 100% !important;
          height: 32px !important;
          padding: 4px 10px !important;
          border-radius: 12px !important;
          box-shadow: none !important;
          min-width: 0 !important;
        }

        .tax-edit-cell { overflow: hidden; }

        .tax-actions .btn,
        .tax-actions button { box-shadow: none !important; }
        .tax-actions { display: inline-flex; gap: 8px; align-items: center; flex-wrap: nowrap; }
        .tax-actions .btn {
          border-radius: 12px !important;
          padding: 6px 12px !important;
          font-weight: 700 !important;
          min-width: 76px;
        }

        .col-sl { width: 70px; }
        .col-id { width: 90px; }
        .col-country { width: 110px; }
        .col-city { width: 140px; }
        .col-service { width: 190px; }
        .col-taxtype { width: 110px; }
        .col-rate { width: 110px; }
        .col-inclusive { width: 120px; }
        .col-base { width: 190px; }
        .col-priority { width: 110px; }
        .col-status { width: 120px; }
        .col-starts { width: 190px; }
        .col-ends { width: 190px; }
        .col-actions { width: 200px; }
      `}</style>

      <div className="card p-4 system-card">
        <h4 className="section-title">Tax System</h4>
        <p className="text-muted mb-4">
          Manage tax rules for different services, including rate, type, and status.
        </p>

        {!readOnly && <form onSubmit={handleSubmitNew} className="settings-form">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Service Type</label>
              <select
                className="form-select"
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                disabled={serviceSelectDisabled}
              >
                <option value="">
                  {serviceLoading
                    ? "Loading services…"
                    : serviceOptions.length
                    ? "Any (no service filter)"
                    : "No services found"}
                </option>
                {serviceOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {serviceError ? (
                <small className="text-danger">{serviceError}</small>
              ) : (
                <small className="text-muted">Fetched from Ride Types API</small>
              )}
            </div>

            <div className="col-md-3">
              <label className="form-label">Country Code</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., BT"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">City ID</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., THIMPHU (optional)"
                value={cityId}
                onChange={(e) => setCityId(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Tax Type</label>
              <select className="form-select" value={taxType} onChange={(e) => setTaxType(e.target.value)}>
                {TAX_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label">Tax Rate (%)</label>
              <input
                type="number"
                className="form-control"
                value={ratePercent}
                onChange={(e) => handleRateChange(e.target.value)}
                onBlur={handleRateBlur}
                min="0"
                max="100"
                step="0.01"
                inputMode="decimal"
              />
              <small className="text-muted">Saved as basis points: {rateBp}</small>
            </div>

            <div className="col-md-4">
              <label className="form-label">Taxable Base</label>
              <select className="form-select" value={taxableBase} onChange={(e) => setTaxableBase(e.target.value)}>
                {TAXABLE_BASES.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label">Tax Type (Inclusive/Exclusive)</label>
              <select className="form-select" value={inclusiveMode} onChange={(e) => setInclusiveMode(e.target.value)}>
                <option value="exclusive">Exclusive (add on top)</option>
                <option value="inclusive">Inclusive (included in price)</option>
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label">Priority (lower wins)</label>
              <input
                type="number"
                className="form-control"
                value={priority}
                onChange={(e) => handlePriorityChange(e.target.value)}
                onBlur={handlePriorityBlur}
                min="1"
                max="10"
                step="1"
                inputMode="numeric"
              />
              <small className="text-muted">Allowed range: 1 to 10</small>
            </div>

            <div className="col-md-4 d-flex align-items-center mt-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="tax-active"
                  className="form-check-input"
                  checked={isActive}
                  onChange={() => setIsActive((v) => !v)}
                />
                <label className="form-check-label" htmlFor="tax-active">Active rule</label>
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Starts At</label>
              <input type="datetime-local" className="form-control" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
              <small className="text-muted">Required</small>
            </div>

            <div className="col-md-6">
              <label className="form-label">Ends At (optional)</label>
              <input type="datetime-local" className="form-control" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
              <small className="text-muted">Leave empty for NULL</small>
            </div>
          </div>

          <button type="submit" className="btn btn-primary w-100 mt-4" disabled={savingNew}>
            {savingNew ? "Saving…" : "Save Tax Rule"}
          </button>
        </form>}

        {/* ---------------- TABLE (LIST) ---------------- */}
        <div className="mt-4" style={wideEdge}>
          <div className="d-flex align-items-center justify-content-between mb-2" style={TOP_PAD}>
            <h5 className="mb-0">Tax Rules</h5>

            <button
              type="button"
              className="btn btn-outline-success btn-sm px-3 export-btn"
              onClick={exportAllToExcel}
              disabled={!taxRules.length}
              style={exportBtnStyle}
              onMouseEnter={() => setIsExportHover(true)}
              onMouseLeave={() => setIsExportHover(false)}
              onFocus={() => setIsExportHover(true)}
              onBlur={() => setIsExportHover(false)}
            >
              Export Excel
            </button>
          </div>

          {error && (
            <div className="alert alert-danger mt-2 mb-3" style={TOP_PAD}>
              <small>{error}</small>
            </div>
          )}

          <div className="table-responsive contrast-surface" style={TOP_PAD}>
            <table className="table table-sm align-middle contrast-table w-100 tax-table">
              <thead>
                <tr>
                  <th className="col-sl">Sl.No</th>
                  <th className="col-id">ID</th>
                  <th className="col-country">Country</th>
                  <th className="col-city">City</th>
                  <th className="col-service">Service Type</th>
                  <th className="col-taxtype">Tax Type</th>
                  <th className="col-rate">Rate (%)</th>
                  <th className="col-inclusive">Inclusive</th>
                  <th className="col-base">Taxable Base</th>
                  <th className="col-priority">Priority</th>
                  <th className="col-status">Status</th>
                  <th className="col-starts">Starts At</th>
                  <th className="col-ends">Ends At</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={14} className="text-muted text-center py-3">Loading…</td>
                  </tr>
                ) : taxRules.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="text-muted text-center py-3">No tax rules found.</td>
                  </tr>
                ) : (
                  taxRules.map((r, idx) => {
                    const isRowEditing = rowEditId === r.id;
                    const busyDelete = deletingId === r.id;
                    const viewRate = ((Number(r.rate_percent_bp) || 0) / 100).toFixed(2);
                    const d = rowDraft || {};
                    const serviceEditDisabled = serviceLoading || !serviceOptions.length;

                    return (
                      <tr key={r.id ?? idx}>
                        <td>{idx + 1}</td>
                        <td className="fw-semibold">{r.id}</td>

                        <td className="tax-edit-cell">
                          {isRowEditing ? (
                            <input className="form-control form-control-sm tax-inline-input" value={d.country_code ?? ""} onChange={(e) => updateDraft("country_code", e.target.value)} />
                          ) : (
                            r.country_code ?? "-"
                          )}
                        </td>

                        <td className="tax-edit-cell">
                          {isRowEditing ? (
                            <input className="form-control form-control-sm tax-inline-input" value={d.city_id ?? ""} onChange={(e) => updateDraft("city_id", e.target.value)} placeholder="optional" />
                          ) : (
                            r.city_id ?? "-"
                          )}
                        </td>

                        <td className="tax-edit-cell">
                          {isRowEditing ? (
                            <select
                              className="form-select form-select-sm tax-inline-select"
                              value={d.service_type ?? ""}
                              onChange={(e) => updateDraft("service_type", e.target.value)}
                              disabled={serviceEditDisabled}
                            >
                              <option value="">
                                {serviceLoading
                                  ? "Loading…"
                                  : serviceOptions.length
                                  ? "Any (no service filter)"
                                  : "No services found"}
                              </option>
                              {serviceOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          ) : (
                            r.service_type ?? "-"
                          )}
                        </td>

                        <td className="tax-edit-cell">
                          {isRowEditing ? (
                            <select className="form-select form-select-sm tax-inline-select" value={d.tax_type ?? "GST"} onChange={(e) => updateDraft("tax_type", e.target.value)}>
                              {TAX_TYPES.map((t) => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          ) : (
                            r.tax_type ?? "-"
                          )}
                        </td>

                        <td className="tax-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm tax-inline-input"
                              value={d.rate_percent ?? "0"}
                              onChange={(e) => updateDraftRate(e.target.value)}
                              onBlur={blurDraftRate}
                              min="0"
                              max="100"
                              step="0.01"
                              inputMode="decimal"
                            />
                          ) : (
                            viewRate
                          )}
                        </td>

                        <td className="tax-edit-cell">
                          {isRowEditing ? (
                            <select className="form-select form-select-sm tax-inline-select" value={Number(d.tax_inclusive) === 1 ? "1" : "0"} onChange={(e) => updateDraft("tax_inclusive", Number(e.target.value))}>
                              <option value="0">No</option>
                              <option value="1">Yes</option>
                            </select>
                          ) : Number(r.tax_inclusive) === 1 ? (
                            "Yes"
                          ) : (
                            "No"
                          )}
                        </td>

                        <td className="tax-edit-cell">
                          {isRowEditing ? (
                            <select className="form-select form-select-sm tax-inline-select" value={d.taxable_base ?? "platform_fee"} onChange={(e) => updateDraft("taxable_base", e.target.value)}>
                              {TAXABLE_BASES.map((b) => (
                                <option key={b} value={b}>{b}</option>
                              ))}
                            </select>
                          ) : (
                            r.taxable_base ?? "-"
                          )}
                        </td>

                        <td className="tax-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm tax-inline-input"
                              value={d.priority ?? "10"}
                              onChange={(e) => updateDraftPriority(e.target.value)}
                              onBlur={blurDraftPriority}
                              min="1"
                              max="10"
                              step="1"
                              inputMode="numeric"
                            />
                          ) : (
                            r.priority ?? "-"
                          )}
                        </td>

                        <td className="tax-edit-cell">
                          {isRowEditing ? (
                            <select className="form-select form-select-sm tax-inline-select" value={Number(d.is_active) === 1 ? "1" : "0"} onChange={(e) => updateDraft("is_active", Number(e.target.value))}>
                              <option value="1">Active</option>
                              <option value="0">Inactive</option>
                            </select>
                          ) : Number(r.is_active) === 1 ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-secondary">Inactive</span>
                          )}
                        </td>

                        <td className="tax-edit-cell">
                          {isRowEditing ? (
                            <input type="datetime-local" className="form-control form-control-sm tax-inline-input" value={d.starts_at ?? ""} onChange={(e) => updateDraft("starts_at", e.target.value)} />
                          ) : (
                            toLocal(r.starts_at)
                          )}
                        </td>

                        <td className="tax-edit-cell">
                          {isRowEditing ? (
                            <input type="datetime-local" className="form-control form-control-sm tax-inline-input" value={d.ends_at ?? ""} onChange={(e) => updateDraft("ends_at", e.target.value)} />
                          ) : (
                            toLocal(r.ends_at)
                          )}
                        </td>

                        <td className="tax-edit-cell">
                          <div className="tax-actions">
                            {!readOnly && (isRowEditing ? (
                              <>
                                <button type="button" className="btn btn-sm btn-outline-success" onClick={saveRowEdit} disabled={rowSaving}>
                                  {rowSaving ? "Saving..." : "Save"}
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancelRowEdit} disabled={rowSaving}>
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => beginRowEdit(r)} disabled={rowEditId && rowEditId !== r.id}>
                                  Edit
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r.id)} disabled={busyDelete || (rowEditId && rowEditId !== r.id)}>
                                  {busyDelete ? "..." : "Delete"}
                                </button>
                              </>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <footer className="dashboard-footer mt-4">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ།</strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}
